import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { resetPasswordUser } from '../userSlice';
import { openAccountModal } from '../accountModalSlice';
import PasswordField from '../AccountModal/PasswordField';

// The server's floor (accounts/consumer/views_password.py). Checked here too so
// a client who mistypes learns it before a round-trip, not after.
const PASSWORD_MIN = 8;

const ERROR_KEYS: Record<string, string> = {
  server: 'auth.errors.server',
  network: 'auth.errors.network',
  unknown: 'auth.errors.unknownReset',
};

/**
 * SC-778 — the page the « définir mon mot de passe » link opens.
 *
 * It serves two arrivals that look the same from here: a client who forgot his
 * password, and one whose account has never had a usable one — created by
 * Google sign-in, or carried over from a previous ordering site. Neither can
 * reach a session any other way, which is why a failed token offers a new link
 * rather than a dead end.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { restaurantUuid } = useParams<{ restaurantUuid: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState<{ password?: string; confirm?: string }>({});
  const [banner, setBanner] = useState<string | null>(null);
  const [expired, setExpired] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const askForANewLink = () => {
    navigate(`/${restaurantUuid}`);
    dispatch(openAccountModal({ mode: 'forgot' }));
  };

  const validate = () => {
    const next: typeof errors = {};
    if (password.length < PASSWORD_MIN) next.password = t('auth.validation.passwordMin8');
    if (confirm !== password) next.confirm = t('auth.validation.passwordMismatch');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    setBanner(null);
    if (!validate()) return;
    setSubmitting(true);
    try {
      await dispatch(
        resetPasswordUser({ token, password, restaurant_uuid: restaurantUuid }),
      ).unwrap();
      // Signed in already — straight to the menu, not back to a login form.
      navigate(`/${restaurantUuid}`);
    } catch (code) {
      const key = String(code);
      if (key === 'invalid_reset_token') setExpired(true);
      else setBanner(t(ERROR_KEYS[key] ?? ERROR_KEYS.unknown));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token || expired) {
    return (
      <div className="max-w-md mx-auto px-4 py-10 space-y-4 text-center">
        <h1 className="font-display text-2xl text-ink">{t('auth.resetInvalidTitle')}</h1>
        <p className="text-sm text-ink/80">{t('auth.resetInvalidBody')}</p>
        <button
          type="button"
          onClick={askForANewLink}
          className="w-full py-3 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px]"
        >
          {t('auth.requestNewLink')}
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-10">
      <form onSubmit={onSubmit} noValidate className="space-y-3">
        <div>
          <h1 className="font-display text-2xl text-ink">{t('auth.resetTitle')}</h1>
          <p className="text-xs text-ink/70 mt-0.5">{t('auth.resetSubtitle')}</p>
        </div>

        {banner && (
          <div className="px-3 py-2 bg-yellow-100 border-2 border-ink text-ink text-sm rounded-btn-sm">
            {banner}
          </div>
        )}

        <label className="flex flex-col">
          <span className="text-xs font-sans font-semibold text-ink mb-1">
            {t('auth.newPasswordLabel')}
          </span>
          <PasswordField
            value={password}
            onChange={setPassword}
            autoComplete="new-password"
            invalid={!!errors.password}
          />
          {errors.password && (
            <span className="text-xs text-red-600 mt-0.5">{errors.password}</span>
          )}
        </label>

        <label className="flex flex-col">
          <span className="text-xs font-sans font-semibold text-ink mb-1">
            {t('auth.confirmPasswordLabel')}
          </span>
          <PasswordField
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
            invalid={!!errors.confirm}
          />
          {errors.confirm && <span className="text-xs text-red-600 mt-0.5">{errors.confirm}</span>}
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full py-2.5 bg-ink text-paper font-display rounded-btn shadow-stamp-sm cursor-pointer transition-transform duration-200 hover:-translate-y-[1px] hover:-translate-x-[1px] disabled:opacity-60 disabled:hover:transform-none"
        >
          {submitting ? t('auth.resetting') : t('auth.resetCta')}
        </button>
      </form>
    </div>
  );
}
